<template>
  <div class="py-2">
    <v-row dense>
      <v-col v-if="explanationText">
        <div class="text-muted">
          {{
            $t("people:PersonProfile.qualifications.qualificationRule.label")
          }}
        </div>
        <div v-html="explanationText" />
      </v-col>
      <v-col class="text-right hidden-sm-and-down">
        <v-btn-icon icon @click="$emit('close')">
          <v-icon sice="large" color="secondary"> $iconTimesCircle </v-icon>
        </v-btn-icon>
      </v-col>
    </v-row>

    <v-divider class="my-2" />
    <v-tooltip location="bottom">
      <template #activator="{ props }">
        <h3 v-bind="props" class="my-3 text-center text-lg-left">
          {{
            $t("people:PersonProfile.qualifications.qualifyingResults.label")
          }}
        </h3>
      </template>
      <span>
        {{ $t("people:PersonProfile.qualifications.qualifyingResults.hint") }}:
        &nbsp;{{ qualifiedResultsTotal }}
      </span>
    </v-tooltip>
    <div class="w-100" style="overflow: auto">
      <table class="entsys--table" style="font-size: 0.95em">
        <thead class="hidden-sm-and-down">
          <tr>
            <th>
              {{ $t("people:PersonProfile.qualifications.date.label") }}
            </th>
            <th>
              {{
                $t(
                  "people:PersonProfile.qualifications.eventOrganiser.label",
                  "Event / Organiser"
                )
              }}
            </th>
            <th>
              {{
                $t(
                  "people:PersonProfile.qualifications.competition.label",
                  "Class"
                )
              }}
            </th>
            <th>
              {{
                $t(
                  "people:PersonProfile.qualifications.competitionLevel.label",
                  "Competition Level"
                )
              }}
            </th>
            <th>
              {{
                $t(
                  "people:PersonProfile.qualifications.difficultyLevel.label",
                  "Difficulty level"
                )
              }}
            </th>
            <th>
              {{
                $t(
                  "people:PersonProfile.qualifications.judgement.label",
                  "Judgement"
                )
              }}
            </th>
            <th>
              {{
                $t("people:PersonProfile.qualifications.result.label", "Result")
              }}
            </th>
            <th>
              {{ $t("people:PersonProfile.qualifications.rank.label", "Rank") }}
            </th>
            <th>
              {{
                $t("people:PersonProfile.qualifications.award.label", "Award")
              }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colspan="99" class="pa-0">
              <v-progress-linear
                :active="$apollo.queries.qualifiedResults.loading"
                indeterminate
                color="primary"
              />
            </td>
          </tr>
          <tr
            v-for="(result, i) in qualifiedResults"
            :key="i"
            class="no-hover hidden-sm-and-down"
          >
            <td>
              {{ $dayjs.date(result.competition.startDate) }}
            </td>
            <td>
              <AppShowEvent
                :event="result.competition.event"
                event-page="EventResultsPage"
                :additional-params="{
                  competitionClass: result.competition.code
                }"
              />
            </td>
            <td class="text-center">
              {{ result.competition.code }}
            </td>
            <td class="text-center">
              <template v-if="result.competition.competitionLevel">
                {{ result.competition.competitionLevel.name }}
              </template>
            </td>
            <td class="text-center">
              <template v-if="result.competition.difficultyLevel">
                {{ result.competition.difficultyLevel.name }}
              </template>
            </td>
            <td class="text-center">
              <template v-if="result.competition.competitionRule">
                {{ result.competition.competitionRule.name }}
              </template>
            </td>
            <td class="text-center">
              <AppShowResultPreview :result="result" />
              <div v-if="result.unqualifiesQualification" class="mt-2">
                <UnqualifiedBadge />
              </div>
            </td>
            <td class="text-center">
              {{ result.rank }}
            </td>
            <td>
              <AppShowResultAward :is-placed="result.isPlaced" />
            </td>
          </tr>
          <tr
            v-for="(result, i) in qualifiedResults"
            :key="`${i}-mobile`"
            class="no-hover hidden-md-and-up pa-0"
          >
            <td colspan="99" class="pa-0">
              <v-row>
                <v-col cols="12">
                  <AppShowEvent
                    :event="result.competition.event"
                    event-page="EventResultsPage"
                    :additional-params="{
                      competitionClass: result.competition.code
                    }"
                  />
                </v-col>

                <v-col cols="6">
                  <strong>
                    {{ $t("people:PersonProfile.qualifications.date.label") }}
                  </strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  {{ $dayjs.date(result.competition.startDate) }}
                </v-col>

                <v-col cols="6">
                  <strong>{{
                    $t(
                      "people:PersonProfile.qualifications.competition.label",
                      "Class"
                    )
                  }}</strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  {{ result.competition.code }}
                </v-col>
                <v-col>
                  <strong>{{
                    $t(
                      "people:PersonProfile.qualifications.competitionLevel.label",
                      "Competition Level"
                    )
                  }}</strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  <template v-if="result.competition.competitionLevel">
                    {{ result.competition.competitionLevel.name }}
                  </template>
                </v-col>
                <v-col cols="6">
                  <strong>{{
                    $t(
                      "people:PersonProfile.qualifications.difficultyLevel.label",
                      "Difficulty level"
                    )
                  }}</strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  <template v-if="result.competition.difficultyLevel">
                    {{ result.competition.difficultyLevel.name }}
                  </template>
                </v-col>
                <v-col cols="6">
                  <strong>{{
                    $t(
                      "people:PersonProfile.qualifications.judgement.label",
                      "Judgement"
                    )
                  }}</strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  <template v-if="result.competition.competitionRule">
                    {{ result.competition.competitionRule.name }}
                  </template>
                </v-col>
                <v-col cols="6">
                  <strong>{{
                    $t(
                      "people:PersonProfile.qualifications.result.label",
                      "Result"
                    )
                  }}</strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  <AppShowResultPreview :result="result" />
                  <div v-if="result.unqualifiesQualification" class="mt-2">
                    <UnqualifiedBadge />
                  </div>
                </v-col>
                <v-col cols="6">
                  <strong>{{
                    $t("people:PersonProfile.qualifications.rank.label", "Rank")
                  }}</strong>
                </v-col>
                <v-col cols="6" class="text-right">
                  {{ result.rank }}
                </v-col>
                <v-col cols="6">
                  <strong>
                    {{
                      $t(
                        "people:PersonProfile.qualifications.award.label",
                        "Award"
                      )
                    }}</strong
                  >
                </v-col>
                <v-col cols="6" class="text-right">
                  <AppShowResultAward :is-placed="result.isPlaced" />
                </v-col>
              </v-row>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import gql from "fraql";
import AppShowResultAward from "@AppShows/AppShowResultAward.vue";
import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
import AppShowResultPreview from "@AppShows/AppShowResultPreview.vue";
import { qualificationStatusIsQualified } from "@AppShows/AppShowQualificationStatus.vue";
import UnqualifiedBadge from "./UnqualifiedBadge.vue";

export default {
  name: "QualificationExpanded",
  components: {
    AppShowResultAward,
    AppShowEvent,
    AppShowResultPreview,
    UnqualifiedBadge
  },
  props: {
    qualification: {
      type: Object,
      required: true
    },
    qualificationRulesStatuses: {
      type: Object,
      required: true
    },
    personId: {
      type: String,
      default: null
    },
    horseId: {
      type: String,
      default: null
    }
  },
  emits: ["close"],
  data() {
    return {
      qualifiedResults: []
    };
  },
  computed: {
    explanationText: ({ qualification }) =>
      qualification?.explanationText ?? "",
    qualifiedResultsTotal: ({ qualifiedResults }) => qualifiedResults.length,
    hasCompletedStatus() {
      const completed = Object.entries(this.qualificationRulesStatuses).find(
        ([, status]) => {
          return qualificationStatusIsQualified(status);
        }
      );
      return completed ? completed[0] : null;
    }
  },
  apollo: {
    qualifiedResults: {
      query: gql`
        query qualifiedResults(
          $qualificationId: ID!
          $qualificationRuleId: ID
          $personId: ID
          $horseId: ID
          $checkDate: Date
          $limit: Int
          $sortBy: String
          $sortDesc: Boolean
        ) {
          qualifiedResults(
            qualificationId: $qualificationId
            qualificationRuleId: $qualificationRuleId
            personId: $personId
            horseId: $horseId
            checkDate: $checkDate
            limit: $limit
            sortBy: $sortBy
            sortDesc: $sortDesc
          ) {
            id
            ${AppShowResultPreview.fragments.result}
            rank
            isPlaced
            unqualifiesQualification(qualificationId: $qualificationId)
            competition {
              id
              code
              startDate
              competitionLevelId
              competitionLevel @client {
                id
                name
              }
              difficultyLevelId
              difficultyLevel @client {
                id
                name
              }
              competitionRuleId 
              competitionRule @client {
                id
                name
              }
              event {
                id
                name
                organiser {
                  id
                  name
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          qualificationId: this.qualification.id,
          qualificationRuleId: this.hasCompletedStatus,
          personId: this.personId,
          horseId: this.horseId,
          checkDate: this.$dayjs().format("YYYY-MM-DD"),
          limit: Boolean(this.hasCompletedStatus) == true ? 5 : 100,
          sortBy: "competitionStartDate",
          sortDesc: true
        };
      },
      fetchPolicy: "cache-and-network"
    }
  }
};
</script>
